import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Chapter, FadeUp } from "@/components/motion";
import { CTAButton } from "@/components/CTA";

const STEPS = [
  { num: "01", title: "NOTIFICA", lead: "Cuéntanos qué ocurre.", desc: "Una descripción del problema y un vídeo de la incidencia. Nada más." },
  { num: "02", title: "RECIBIMOS", lead: "Aprea recibe y analiza.", desc: "Estudiamos la incidencia y te respondemos con la mayor brevedad posible." },
  { num: "03", title: "ASIGNAMOS", lead: "Activamos al especialista.", desc: "Comunicamos la incidencia al técnico especializado correspondiente." },
  { num: "04", title: "ACTUAMOS", lead: "Comienza la cuenta atrás.", desc: "Gestionamos para disponer de un técnico en tu comercio en menos de 24 horas hábiles." },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.7", "end 0.6"] });
  const line = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section ref={ref} className="relative py-32 lg:py-44" data-testid="section-proceso">
      <div className="mx-auto max-w-[1500px] px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-40">
            <Chapter num="03" label="CÓMO FUNCIONA" className="mb-12" />
            <h2 className="font-display font-800 uppercase leading-[0.98] tracking-tight text-4xl sm:text-6xl text-offwhite">
              Cuatro pasos.
              <br />
              <span className="font-cormorant normal-case font-medium text-metal">Cero gestiones tuyas.</span>
            </h2>
            <FadeUp delay={0.15} className="mt-10 flex flex-wrap gap-5 items-center">
              <CTAButton testid="process-cta-alta">EMPEZAR AHORA</CTAButton>
              <Link
                to="/como-funciona"
                data-testid="process-link-detail"
                className="group inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-mist hover:text-silver transition-colors duration-300"
              >
                VER EL PROCESO COMPLETO
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeUp>
          </div>
        </div>

        <div className="lg:col-span-7 relative pl-10">
          <span className="absolute left-0 top-2 bottom-2 w-px bg-silver/10" />
          <motion.span
            style={{ scaleY: line }}
            className="absolute left-0 top-2 bottom-2 w-px bg-glow/70 origin-top"
          />
          <div className="space-y-16">
            {STEPS.map((step, i) => (
              <FadeUp key={step.num} delay={i * 0.06}>
                <div className="group relative border border-silver/10 bg-panel/40 p-8 md:p-10 hover:border-silver/30 transition-colors duration-500">
                  <span className="absolute -left-10 top-10 h-2.5 w-2.5 rotate-45 bg-night border border-glow/70" />
                  <div className="flex items-baseline justify-between gap-6 mb-5">
                    <span className="font-oswald font-600 uppercase text-2xl md:text-4xl tracking-wide text-offwhite">
                      {step.title}
                    </span>
                    <span className="font-oswald font-700 text-5xl md:text-6xl text-outline group-hover:text-outline transition-all duration-500">
                      {step.num}
                    </span>
                  </div>
                  <p className="font-cormorant text-xl md:text-2xl text-silver mb-3">{step.lead}</p>
                  <p className="text-mist text-sm md:text-base leading-relaxed max-w-md">{step.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
